import React from 'react'
import { useSelector } from 'react-redux'
import { Observable } from 'rx'
import { calculateMultiProjectedPay } from '@mobi/betslip/helpers/calculator/multis'
import { isCampaignRedeemableWithBettingType } from '@mobi/betslip/helpers/campaignHelper'
import {
  isAllUpSelection,
  isKambiSportsSelection,
  isNoveltyBetType,
  isToteSelection,
} from '@mobi/betslip/helpers/typeGuards'
import type { BettingType, Campaign, MultiInvestment, Selection } from '@mobi/betslip/types'
import { Grid, GridCell, GridRow } from '@mobi/component-library/Legacy/Grid'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { calculateKambiSelectionBoostedPriceWithReward } from '@mobi/sports-offering/helpers/currency/calculator'
import { Currency, toMoney } from '@mobi/utils/money'
import { selectIsBusy } from '@core/Areas/QuickDeposit/Store/selectors'
import { QuickbetState, state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import {
  getPriceFromKambiSportsSelection,
  isKambiCashOutAvailable,
  isKambiEarlySettlementAvailable,
} from '@core/Areas/Quickbet/helpers/selectionsKambi'
import {
  SetActiveInvestment,
  SetAllowInvestmentState,
  SetBonusBet,
  ToggleBonusBetUsage,
} from '@core/Areas/Quickbet/signals'
import { ClearInsufficientFundsForBet } from '@core/Areas/Quickbet/signals'
import { calculateCombinations as tippingCombinations } from '@core/Areas/Tipping/helpers/calculator'
import { observeImmutable } from '@core/Components/HOCs'
import { isToteSportsSelection } from '@core/Data/Betting/selections'
import { trackBonusBetEvent } from '@core/Services/Analytics/helpers/trackers'
import { keys } from '@core/Services/Analytics/keys'
import {
  UserAccountState,
  state$ as userAccountState$,
} from '@core/State/UserAccount/userAccountDriver'
import { useObservableImmutable } from '@core/Utils/hooks'
import EachWayCheckbox from '../EachWay/EachWayCheckbox'
import { FormulaState, state$ as formulaState$ } from '../Formula/driver'
import { SuperPickState, state$ as superPickState$ } from '../SuperPick/Components/SuperPick/driver'
import { ResetInvalidSuperPickSelection } from '../SuperPick/Components/SuperPick/signals'
import {
  BetInvestmentGroupStyled,
  BetInvestmentStyled,
  InvestmentContainerStyled,
  InvestmentStyled,
  TotalCostMoneyStyled,
  TotalCostSectionStyled,
} from './BetInvestment.styles'
import {
  BonusBetButton,
  FlexiDisplay,
  LegInfo,
  NumberOfCombinationsDisplay,
  ProjectedReturn,
  ProjectedReturnComponent,
} from './Components'
import { CashOutBadge, EarlyPayoutBadge } from './Components/Badges/QuickbetBadges'
import {
  type BonusBet,
  type InvestmentState,
  type InvestmentType,
  state$ as investmentState$,
} from './driver'

export interface BetInvestmentStateProps {
  winValue: number
  placeValue: number
  selection: Selection | null
  selectionMultiLegs: QuickbetState['selectionMultiLegs']
  isBusy: boolean
  canConfirmBet: boolean
  canBet: boolean
  isUserInputAllowed: boolean
  isEachWayAvailable: boolean
  bettingType: BettingType
  shouldAllowPlaceInvestment: boolean
  shouldAllowWinInvestment: boolean
  activeInvestment: InvestmentType
  isSuperPickSelectionInvalid: boolean
  numberOfCombinationsSelected: number
  presetInvestment?: boolean
  activeCampaigns: Campaign[] | null
  isWinBonusBet: boolean
  isPlaceBonusBet: boolean
  isUsingBonusBet: boolean
  isUsingBonusCash: boolean
  selectedBonusBet: BonusBet | undefined
  selectedKambiReward: QuickbetState['selectedKambiReward']
}

const betInvestmentState$ = Observable.combineLatest(
  quickbetState$,
  investmentState$,
  superPickState$,
  formulaState$,
  userAccountState$,
  (
    quickbetRecord,
    investmentRecord,
    superPickRecord,
    formulaRecord,
    userAccountState: UserAccountState
  ): BetInvestmentStateProps => {
    const quickbetState = quickbetRecord.toJS() as QuickbetState
    const investmentState = investmentRecord.toJS() as InvestmentState
    const superPickState = superPickRecord.toJS() as SuperPickState
    const formulaState = formulaRecord.toJS() as FormulaState
    return {
      winValue: investmentState.win.value,
      placeValue: investmentState.place.value,
      selection: quickbetState.selection,
      selectionMultiLegs: quickbetState.selectionMultiLegs,
      isBusy: quickbetState.isBusy,
      canConfirmBet: quickbetState.canConfirmBet,
      canBet: quickbetState.canBet,
      isUserInputAllowed: quickbetState.canChangeInvestment,
      isEachWayAvailable: quickbetState.isEachWayAvailable,
      bettingType: quickbetState.bettingType as BettingType,
      shouldAllowPlaceInvestment: quickbetState.shouldAllowPlaceInvestment,
      shouldAllowWinInvestment: quickbetState.shouldAllowWinInvestment,
      activeInvestment: investmentState.activeInvestment,
      isSuperPickSelectionInvalid: !!superPickState.invalidSelectionMessage,
      numberOfCombinationsSelected: formulaState.numberOfCombinationsSelected,
      presetInvestment: quickbetState.presetInvestment ? true : undefined,
      activeCampaigns: userAccountState.activeCampaigns?.toArray() ?? null,
      isWinBonusBet: investmentState.win.isBonusBet ?? false,
      isPlaceBonusBet: investmentState.place.isBonusBet ?? false,
      isUsingBonusBet: quickbetState.isUsingBonusBet,
      isUsingBonusCash: quickbetState.isUsingBonusCash,
      selectedBonusBet: investmentState.bonusBet,
      selectedKambiReward: quickbetState.selectedKambiReward,
    }
  }
)

export const BetInvestmentComponent: React.FC<BetInvestmentStateProps> = ({
  winValue,
  placeValue,
  selection,
  selectionMultiLegs,
  isBusy,
  canConfirmBet,
  canBet,
  isUserInputAllowed,
  isEachWayAvailable,
  activeInvestment,
  bettingType,
  shouldAllowWinInvestment,
  shouldAllowPlaceInvestment,
  isSuperPickSelectionInvalid,
  numberOfCombinationsSelected: numberOfFormulasSelected,
  presetInvestment,
  activeCampaigns,
  isWinBonusBet,
  isPlaceBonusBet,
  isUsingBonusBet,
  isUsingBonusCash,
  selectedBonusBet,
  selectedKambiReward,
}) => {
  const [originalState] = React.useState<{
    shouldAllowWinInvestment: boolean
    shouldAllowPlaceInvestment: boolean
  }>({ shouldAllowPlaceInvestment, shouldAllowWinInvestment })

  React.useLayoutEffect(() => {
    SetAllowInvestmentState({
      shouldAllowWinInvestment: !isPlaceBonusBet && originalState.shouldAllowWinInvestment,
      shouldAllowPlaceInvestment: !isWinBonusBet && originalState.shouldAllowPlaceInvestment,
    })
  }, [isPlaceBonusBet, isWinBonusBet, originalState])

  const {
    isLoggedIn = null,
    accountNumber = null,
    accountBalance = null,
    bonusBetBalance = null,
    bonusCashBalance = null,
  } = useObservableImmutable(userAccountState$, [
    'isLoggedIn',
    'accountBalance',
    'accountNumber',
    'bonusBetBalance',
    'bonusCashBalance',
  ])

  const hasBonusBet = activeCampaigns?.some(campaign => campaign.rewardType === 'BonusBetReward')
  const hasBonusCash = activeCampaigns?.some(
    campaign =>
      campaign.rewardType === 'BonusCashReward' &&
      isCampaignRedeemableWithBettingType(campaign, bettingType)
  )

  const isEligibleForBonusBet = !!hasBonusBet && !hasBonusCash
  React.useEffect(() => {
    if (isLoggedIn && isEligibleForBonusBet) {
      trackBonusBetEvent(keys.quickbetBonusBetButtonShown, {
        accountNumber: accountNumber ?? '',
        accountBalance,
        bonusBetBalance,
        bonusCashBalance,
      })
    }
  }, [
    isLoggedIn,
    isEligibleForBonusBet,
    accountNumber,
    accountBalance,
    bonusBetBalance,
    bonusCashBalance,
  ])

  if (!canBet) {
    return null
  }

  const changeToWinInvestment = () => {
    if (isBusy) return
    ClearInsufficientFundsForBet()
    SetActiveInvestment('win')
    ResetInvalidSuperPickSelection()
  }

  const changeToPlaceInvestment = () => {
    if (isBusy) return
    ClearInsufficientFundsForBet()
    SetActiveInvestment('place')
    ResetInvalidSuperPickSelection()
  }

  const isWinActive =
    isSuperPickSelectionInvalid || (isUserInputAllowed && activeInvestment === 'win')
  const isPlaceActive =
    isSuperPickSelectionInvalid || (isUserInputAllowed && activeInvestment === 'place')
  const isFixedOddsRacing = bettingType === 'fixed-odds-racing'
  const isToteRacing = bettingType === 'tote-racing'
  const isKambiSports = !isFixedOddsRacing && !isToteRacing && isKambiSportsSelection(selection)
  const isCashOut = isKambiSports && isKambiCashOutAvailable(selection)
  const isEarlyPayout = isKambiSports && isKambiEarlySettlementAvailable(selection)

  const shouldDisplayFlexi = isToteSelection(selection) && isNoveltyBetType(selection.betType)
  const shouldDisplayNumberOfCombinations =
    isAllUpSelection(selection) || isToteSportsSelection(selection)

  const numberOfCombinations = isAllUpSelection(selection)
    ? numberOfFormulasSelected
    : isToteSelection(selection)
      ? selection.numberOfCombinations
      : isToteSportsSelection(selection)
        ? tippingCombinations(selection.betSelections)
        : 1
  const hasSecondaryDisplay =
    shouldAllowPlaceInvestment || shouldDisplayFlexi || shouldDisplayNumberOfCombinations

  if (presetInvestment) {
    return null
  }

  const toggleBonusBet = () => {
    ToggleBonusBetUsage()
    SetBonusBet(null)
  }

  const trackBonusBetClick = () => {
    trackBonusBetEvent(
      isUsingBonusBet
        ? keys.quickbetBonusBetButtonDeactivated
        : keys.quickbetBonusBetButtonActivated,
      {
        accountNumber: accountNumber ?? '',
        accountBalance: accountBalance,
        bonusBetBalance: bonusBetBalance,
        bonusCashBalance: bonusCashBalance,
      }
    )
  }

  let shouldDisplayBonusButton = false
  if (!!hasBonusBet || !!hasBonusCash) {
    if (isUsingBonusCash) {
      // We are checking for tote here as bonus cash isn't available and bonus bets are n/a for tote
      if (hasBonusCash || isToteRacing) {
        shouldDisplayBonusButton = false
      } else {
        shouldDisplayBonusButton = true
      }
    } else {
      shouldDisplayBonusButton = true
    }
  }

  const isMultiSelection = selectionMultiLegs && selectionMultiLegs.length > 0
  const isBonusBetSelected = !!selectedBonusBet

  const kambiSportsProjectedReturn = isKambiSports
    ? new Currency(
        selectedKambiReward
          ? calculateKambiSelectionBoostedPriceWithReward(
              getPriceFromKambiSportsSelection(selection),
              selectedKambiReward
            )
          : getPriceFromKambiSportsSelection(selection)
      ).multiply(winValue).value
    : null

  return (
    <BetInvestmentStyled data-tid-quickbet-investments=''>
      <Grid padding='0.4rem'>
        {shouldAllowWinInvestment ? (
          <GridCell width={hasSecondaryDisplay ? '50%' : 'auto'}>
            <InvestmentComponent
              onClick={changeToWinInvestment}
              isActive={isWinActive}
              investmentType='win'
              amount={winValue}
              shouldFontResize={hasSecondaryDisplay}
              isBonusBet={isWinBonusBet}
              tabIndex={0}
              aria-label={`Betting ${toMoney(winValue)} to win`}
            />
          </GridCell>
        ) : null}

        {shouldAllowPlaceInvestment ? (
          <GridCell width={shouldAllowWinInvestment ? '50%' : 'auto'}>
            <InvestmentComponent
              onClick={changeToPlaceInvestment}
              isActive={isPlaceActive}
              investmentType='place'
              amount={placeValue}
              shouldFontResize={hasSecondaryDisplay}
              isBonusBet={isPlaceBonusBet}
              tabIndex={1}
              aria-label={`Betting ${toMoney(placeValue)} to place`}
            />
          </GridCell>
        ) : null}

        {shouldDisplayFlexi ? (
          <GridCell width='50%'>
            <FlexiDisplay winInvestment={winValue} numberCombinations={numberOfCombinations} />
          </GridCell>
        ) : null}

        {shouldDisplayNumberOfCombinations ? (
          <GridCell width='50%'>
            <NumberOfCombinationsDisplay
              numberOfCombinations={numberOfCombinations}
              data-tid-quickbet-investments-combos=''
            />
          </GridCell>
        ) : null}
      </Grid>

      {isFixedOddsRacing || isToteRacing ? (
        <Grid margin='0 0 -1.5rem 0'>
          <GridRow padding='0'>
            <GridCell valign='middle'>
              {shouldDisplayBonusButton ? (
                <GridRow padding='1rem 0 1rem 0'>
                  <GridCell valign='middle' width='100%'>
                    <BonusBetButton
                      toggleBonusBet={toggleBonusBet}
                      trackBonusBetClick={trackBonusBetClick}
                    />
                  </GridCell>
                </GridRow>
              ) : null}

              {!isBonusBetSelected && isEachWayAvailable && !canConfirmBet ? (
                <GridRow padding='1rem 0 1rem 0'>
                  <GridCell valign='middle' width='100%'>
                    <EachWayCheckbox />
                  </GridCell>
                </GridRow>
              ) : null}
            </GridCell>

            {isMultiSelection ? (
              <GridCell valign='top'>
                <ProjectedReturnComponent
                  value={calculateMultiProjectedPay(
                    selectionMultiLegs,
                    { value: winValue } as MultiInvestment,
                    true
                  )}
                />
              </GridCell>
            ) : (
              <GridCell valign='top'>
                <ProjectedReturn />
              </GridCell>
            )}
          </GridRow>
        </Grid>
      ) : null}

      {isKambiSports && (
        <div style={{ marginBottom: '-1.5rem' }}>
          <Flex justifyContent='space-between'>
            <Flex gap='1rem' alignItems='center'>
              {isCashOut && <CashOutBadge />}
              {isEarlyPayout && <EarlyPayoutBadge />}
            </Flex>

            <ProjectedReturnComponent value={kambiSportsProjectedReturn} />
          </Flex>
        </div>
      )}

      {shouldDisplayNumberOfCombinations ? (
        <TotalCostSectionStyled data-tid-quickbet-investments-total-cost-title=''>
          Total Cost
          <TotalCostMoneyStyled data-tid-quickbet-investments-total-cost=''>
            ${(winValue * (numberOfCombinations || 0)).toFixed(2)}
          </TotalCostMoneyStyled>
        </TotalCostSectionStyled>
      ) : null}
    </BetInvestmentStyled>
  )
}

export type InvestmentComponentProps = {
  onClick: () => void
  isActive: boolean
  investmentType: InvestmentType
  amount: number
  shouldFontResize: boolean
  isBonusBet: boolean
  ariaLabel?: string
} & Pick<React.HTMLAttributes<HTMLElement>, 'tabIndex' | 'aria-label'>

export const BetInvestment: React.ComponentClass = observeImmutable(
  betInvestmentState$,
  ({ record }) => {
    const quickBetDepositing = useSelector(selectIsBusy)
    return <BetInvestmentComponent {...record} isBusy={record.isBusy || quickBetDepositing} />
  }
)
BetInvestment.displayName = 'BetInvestment'

// ================
// Local Components
// ================

const InvestmentComponent: React.FC<InvestmentComponentProps> = ({
  onClick,
  isActive,
  investmentType,
  amount,
  shouldFontResize,
  isBonusBet = false,
  ...htmlAttributes
}) => {
  return (
    <section
      onClick={isBonusBet ? () => null : onClick}
      role='button'
      {...htmlAttributes}
      data-testid='Quickbet.Investment'
    >
      <BetInvestmentGroupStyled isActive={isActive}>
        <LegInfo investmentType={investmentType} isActive={isActive} />

        <InvestmentContainerStyled isBonusBet={isBonusBet} isActive={isActive}>
          <InvestmentStyled shouldFontResize={shouldFontResize} amount={amount} decimalPlaces={2} />
        </InvestmentContainerStyled>
      </BetInvestmentGroupStyled>
    </section>
  )
}
